import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import useScreenSize from '../../../../hooks/useScreenSize';
import {
  behaviorSettingsProps,
  generateClassNameByBehaviorSettings,
} from '../../../../utils/behaviorSettings';
import { formattedDate, formattedDateByFormat } from '../../../../utils/date-formatter.js';
import Link, { propTypes as linkPropTypes } from '../../../01_atoms/Link';
import Button from '../../../01_atoms/Button';
import Slider from '../../../01_atoms/Slider';
import FormattedText from '../../../01_atoms/FormattedText';

import styles from './index.module.scss';

const ICONS = {
  tick: { src: '/static/icons/teal-tick.svg', alt: 'Successful appeal' },
  exclamation_mark: { src: '/static/icons/pink-exclamation.svg', alt: 'Active appeal' },
};

const getIcon = (item) =>
  ICONS[item.icon] || (item.isActiveAppeal ? ICONS.exclamation_mark : ICONS.tick);

const getTag = (item) => item.tag || (item.isActiveAppeal ? 'Active appeal' : 'Completed appeal');

const getDate = (item) =>
  formattedDateByFormat(item.timestamp, item.dateFormat) || formattedDate(item.timestamp);

const BBImpactTimeline = ({ title, items, behaviorSettings, uuid }) => {
  const [state, setState] = useState({
    sliderSettings: {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToScroll: 1,
      navClass: 'bb-impact-timeline--navigation',
    },
    isInitialized: false,
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    popupItemIndex: null,
  });
  const { width } = useScreenSize();

  const onPopupShow = useCallback((popupItemIndex) => {
    setState((prev) => ({ ...prev, popupItemIndex }));
  }, []);

  const onPopupHide = useCallback(() => {
    setState((prev) => ({ ...prev, popupItemIndex: null }));
  }, []);

  const onEscKeyPress = useCallback(
    (event) => {
      if (event.keyCode === 27) {
        onPopupHide();
      }
    },
    [onPopupHide],
  );

  if (width >= 992 && !state.isDesktop) {
    setState((prev) => ({
      ...prev,
      isInitialized: true,
      isDesktop: true,
      isTablet: false,
      isMobile: false,
      sliderSettings: {
        ...prev.sliderSettings,
        slidesToShow: 3,
        initialSlide: items.length - 3,
      },
    }));
  }

  if (width >= 768 && width < 992 && !state.isTablet) {
    setState((prev) => ({
      ...prev,
      isInitialized: true,
      isDesktop: false,
      isTablet: true,
      isMobile: false,
      sliderSettings: {
        ...prev.sliderSettings,
        slidesToShow: 2,
        initialSlide: items.length - 2,
      },
    }));
  }

  if (width < 768 && !state.isMobile) {
    setState((prev) => ({
      ...prev,
      isInitialized: true,
      isDesktop: false,
      isTablet: false,
      isMobile: true,
      sliderSettings: {
        ...prev.sliderSettings,
        slidesToShow: 1,
        initialSlide: items.length - 1,
      },
    }));
  }

  useEffect(() => {
    window.addEventListener('keydown', onEscKeyPress, false);

    return () => {
      window.removeEventListener('keydown', onEscKeyPress, false);
    };
  }, [onEscKeyPress]);

  if (!state.isInitialized) {
    return null;
  }

  const classes = [
    'bb',
    styles['bb-impact-timeline'],
    generateClassNameByBehaviorSettings(behaviorSettings),
  ];

  return (
    <div className={classes.join(' ')} id={uuid}>
      <div className="container">
        {title && <h2>{title}</h2>}

        <Slider {...state.sliderSettings}>
          {items.map((item, index) => (
            <div className={styles['bb-impact-timeline__item']} key={item.title}>
              {item.previewImage && (
                <div
                  // The "bb-impact-timeline--popup" classname is in use by GTM.
                  className={`${styles['bb-impact-timeline__item-image']} bb-impact-timeline--popup`}
                  onClick={() => (!state.isMobile ? onPopupShow(index) : null)}
                  onKeyDown={(e) =>
                    !state.isMobile && e.key === 'Enter' ? onPopupShow(index) : null
                  }
                >
                  <img
                    src={item.previewImage.url}
                    alt={item.previewImage.alt}
                    width={90}
                    height={90}
                  />

                  <div
                    // The "popover d-none d-md-block" classnames are in use by GTM.
                    className="popover d-none d-md-block"
                  />
                </div>
              )}

              <div
                className={`${styles['bb-impact-timeline__item-status']} ${item.isActiveAppeal ? styles['bb-impact-timeline__item-status--active'] : ''}`}
              >
                {getTag(item)}
              </div>

              {item.title && (
                <div className={styles['bb-impact-timeline__item-title']}>{item.title}</div>
              )}

              {item.previewDescription && (
                <FormattedText
                  className={styles['bb-impact-timeline__item-short-description']}
                  text={item.previewDescription}
                />
              )}

              {item.nextLink && (
                <Link {...item.nextLink}>
                  <Button
                    href={item.nextLink.url}
                    tag="a"
                    className={`${styles['bb-impact-timeline__item-cta']} bb-impact-timeline--button`}
                    size="small"
                  >
                    Support this appeal
                  </Button>
                </Link>
              )}

              <div className={styles['bb-impact-timeline__item-spot']}>
                <span className={styles['bb-impact-timeline__item-spot-border']} />
                <span className={styles['bb-impact-timeline__item-spot-circle']}>
                  <img src={getIcon(item).src} alt={getIcon(item).alt} />
                </span>
                <span className={styles['bb-impact-timeline__item-spot-date']}>
                  {getDate(item)}
                </span>
              </div>
            </div>
          ))}
        </Slider>

        <div className={styles['bb-impact-timeline__timeline']}>
          <div className={styles['bb-impact-timeline__start']} />
          <div className={styles['bb-impact-timeline__end']} />
        </div>

        {state.popupItemIndex !== null && (
          <div className={styles['bb-impact-timeline__popup']}>
            <div className={styles['bb-impact-timeline__popup-body']}>
              <span
                className={styles['bb-impact-timeline__popup-close-btn']}
                onClick={onPopupHide}
                onKeyDown={onPopupHide}
              />

              <img
                className={styles['bb-impact-timeline__popup-image']}
                src={items[state.popupItemIndex].image.url}
                alt={items[state.popupItemIndex].image.alt}
              />

              <div className={styles['bb-impact-timeline__popup-content']}>
                <p className={styles['bb-impact-timeline__popup-title']}>
                  <strong>{items[state.popupItemIndex].popupTitle}</strong>
                </p>

                <FormattedText
                  className={styles['bb-impact-timeline__popup-description']}
                  text={items[state.popupItemIndex].description}
                />

                {items[state.popupItemIndex].nextLink && (
                  <Link {...items[state.popupItemIndex].nextLink}>
                    <Button
                      href={items[state.popupItemIndex].nextLink.url}
                      tag="a"
                      className="bb-impact-timeline-popup--button"
                    >
                      Support this appeal
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

BBImpactTimeline.propTypes = {
  title: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      popupTitle: PropTypes.string,
      tag: PropTypes.string,
      icon: PropTypes.oneOf(['tick', 'exclamation_mark', '']),
      dateFormat: PropTypes.string,
      previewDescription: PropTypes.string,
      previewImage: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
      description: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string,
      }),
      nextLink: PropTypes.shape(linkPropTypes),
      isActiveAppeal: PropTypes.bool,
      timestamp: PropTypes.number,
    }),
  ).isRequired,
  behaviorSettings: behaviorSettingsProps,
  uuid: PropTypes.string,
};

BBImpactTimeline.defaultProps = {
  title: '',
  behaviorSettings: null,
  uuid: null,
};

export default BBImpactTimeline;
